<template>
    <div>
        <v-card class="categories" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('categories.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn color="white" class="minw100" outlined @click="openForm('add')">
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text class="card-body">
                <v-data-table
                    :headers="headers"
                    :items="categories"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    disable-sort
                    disable-pagination
                    hide-default-footer
                    class="operations striped"
                >
                    <template slot="item" slot-scope="props">
                        <tr>
                            <td>{{ props.item.name }}</td>
                            <td v-if="!isAllegra">{{ props.item.type ? $t(`general.${props.item.type}`) : '' }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openForm('edit', props.item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteCategory(props.item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">{{
                                                $t('general.delete')
                                            }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <cod-dialog id="category-form" :show.sync="formControl.visible">
            <template slot="header">
                <span v-if="formControl.mode == 'add'">{{ $t('general.add_category') }}</span>
                <span v-if="formControl.mode == 'edit'">{{ $t('categories.edit') }}</span>
            </template>

            <div class="pa-5" slot="content">
                <v-form ref="form">
                    <v-text-field
                        ref="nameField"
                        v-model="form.name"
                        outlined
                        :label="$t('general.name')"
                        :rules="form.rules.name"
                        :error-messages="form.errors.name"
                        autofocus
                        validate-on-blur
                        autocomplete="off"
                        @input="
                            $refs.nameField.validate();
                            delete form.errors.name;
                        "
                    />

                    <v-select
                        v-if="!isAllegra"
                        v-model="form.type"
                        outlined
                        :items="appConfig.product.types"
                        :label="$t('categories.type')"
                        :rules="form.rules.type"
                        :error-messages="form.errors.type"
                        validate-on-blur
                    >
                        <template slot="selection" slot-scope="data">
                            <label>{{ $t(`general.${data.item}`) }}</label>
                        </template>
                        <template slot="item" slot-scope="data">
                            <label>{{ $t(`general.${data.item}`) }}</label>
                        </template>
                    </v-select>
                </v-form>
            </div>

            <template slot="footer">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    outlined
                    color="primary"
                    :disabled="form.busy"
                    @click="formControl.visible = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    depressed
                    :disabled="form.busy"
                    :loading="form.busy"
                    class="minw100 flex-1 ml-1"
                    @click="saveCategory()"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </template>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';

export default {
    components: {
        CodDialog
    },

    created() {
        loadData('/admin/load/categories').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            categories: [],
            loading: false,

            form: this.createForm(),

            formControl: {
                mode: 'add',
                visible: false
            }
        };
    },

    computed: {
        headers() {
            const headers = [{ text: this.$t('general.name'), value: 'name' }]

            if(!this.isAllegra) {
                headers.push({ text: this.$t('categories.type'), value: 'type' })
            }

            headers.push({ text: '' })

            return headers
        }
    },

    methods: {
        onDataLoaded(data) {
            this.categories = data.categories;
        },

        // Create category form
        createForm(category = null) {
            return new CodForm({
                id: category ? category.id : null,
                name: category ? category.name : null,
                type: category ? category.type : null,

                rules: {
                    name: [CodForm.rule('required')],
                    type: this.Allegra ? [] : [CodForm.rule('required')],
                }
            });
        },

        // Open form to add or edit a category
        openForm(mode = 'add', category = null) {
            this.form = this.createForm(category);

            this.formControl.mode = mode;
            this.formControl.visible = true;

            this.$nextTick(() => {
                if(this.$refs.form) this.$refs.form.resetValidation();

                this.$refs.nameField.focus();
            });
        },

        // Save new category to the server
        saveCategory() {
            if (!this.$refs.form.validate()) return

            if (this.formControl.mode !== 'edit') {
                ApiService.post('/admin/categories', this.form).then(response => {
                    this.formControl.visible = false;
                    this.categories.push(response.data);
                    this.$snackbar(this.$t('general.category_saved'), 'success');
                });
            } else {
                ApiService.patch(`/admin/categories/${this.form.id}`, this.form).then(response => {
                    this.formControl.visible = false;
                    this.categories.forEach((category, index, array) => {
                        if (category.id === this.form.id) {
                            this.$set(array, index, response.data);
                            this.categories[index].type = this.form.type;
                        }
                    });
                    this.$snackbar(this.$t('categories.updated'), 'success');
                });
            }
        },

        // Delete category from the server
        deleteCategory(category) {
            this.$confirm(
                this.$t('categories.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    this.formControl.visible = false;
                    ApiService.delete(`/admin/categories/${category.id}`, {})
                        .then(response => {
                            this.categories = this.categories.filter(item => item.id !== category.id);
                            this.$snackbar(this.$t('categories.deleted'), 'success');
                        })
                        .catch(error => {
                            if (error.response.status === 400) {
                                error.response.data.errors.forEach(item => {
                                    if (item.code === 'has_products') {
                                        this.$snackbar(this.$t('categories.has_products'), 'error');
                                    }
                                });
                            }
                        });
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.categories {
    max-width: 600px;

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .card-body {
        margin-top: 16px;
    }
}
</style>
